import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Mail, Phone, Calendar, ArrowLeft, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BusinessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    contact_name: '',
    contact_email: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchUser();
    fetchBusiness();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // User not logged in
    }
  };

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${id}`);
      setBusiness(response.data);
    } catch (error) {
      console.error('Error fetching business:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContact = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate('/chat', { state: { businessId: business.business_id, targetUserId: business.user_id } });
  };

  const handleSpeakToOwner = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setContactForm({
      contact_name: user.name,
      contact_email: user.email || '',
      message: ''
    });
    setShowContactModal(true);
  };

  const handleSendEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await axios.post(
        `${API}/businesses/${business.business_id}/contact-owner`,
        contactForm,
        { withCredentials: true }
      );
      toast.success('Enquiry sent! The business owner will be notified via email.');
      setShowContactModal(false);
      setContactForm({ contact_name: '', contact_email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send enquiry. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-business-detail">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-orange"></div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="business-not-found">
        <div className="text-center">
          <p className="text-xl text-slate-600">Business not found</p>
          <Button onClick={() => navigate('/directory')} className="mt-4">
            Back to Directory
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="business-detail-page">
      <div className="container mx-auto px-6 md:px-12 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-primary hover:bg-primary/5"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back
        </Button>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-primary p-8 text-white">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-4xl font-outfit font-bold mb-2">{business.name}</h1>
                <p className="text-lg text-white/80">{business.industry}</p>
              </div>
              {business.is_verified && (
                <span className="px-4 py-2 bg-accent-cyan text-white text-sm font-medium rounded-full">
                  Verified
                </span>
              )}
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h2 className="text-2xl font-outfit font-semibold text-primary mb-4">About</h2>
                  <p className="text-slate-600 leading-relaxed">{business.philosophy}</p>
                </div>

                {/* Tags Section */}
                {business.tags && business.tags.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-outfit font-semibold text-primary mb-4">Business Tags</h2>
                    <div className="flex flex-wrap gap-2">
                      {business.tags.map((tag) => (
                        <Badge 
                          key={tag}
                          className="bg-accent-orange/10 text-accent-orange px-4 py-2 text-sm"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h2 className="text-2xl font-outfit font-semibold text-primary mb-4">Experience</h2>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-5 w-5 text-accent-orange" />
                    <span>{business.years_experience} years in the industry</span>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-outfit font-semibold text-primary mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-accent-orange" />
                      <span className="text-slate-600">{business.contact_email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-accent-orange" />
                      <span className="text-slate-600">{business.contact_phone}</span>
                    </div>
                  </div>
                </div>

                {business.owner && (
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="text-lg font-outfit font-semibold text-primary mb-4">Owner</h3>
                    <p className="text-slate-600">{business.owner.name}</p>
                    <p className="text-sm text-slate-500">{business.owner.email}</p>
                  </div>
                )}

                <Button
                  onClick={handleContact}
                  className="w-full bg-accent-orange text-white hover:bg-[#D97000] rounded-full py-6"
                  data-testid="start-conversation-button"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Start Conversation
                </Button>

                {business.is_verified && user && business.user_id !== user.user_id && (
                  <Button
                    onClick={handleSpeakToOwner}
                    variant="outline"
                    className="w-full border-accent-cyan text-accent-cyan hover:bg-accent-cyan hover:text-white rounded-full py-6 mt-4"
                    data-testid="speak-to-owner-button"
                  >
                    <Mail className="mr-2 h-5 w-5" />
                    Speak to an Owner
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Owner Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-outfit font-bold text-primary">
              Send Enquiry to {business?.name}
            </DialogTitle>
            <DialogDescription>
              The business owner will receive your enquiry via email and can respond directly.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendEnquiry} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="contact_name">Your Name</Label>
              <Input
                id="contact_name"
                value={contactForm.contact_name}
                onChange={(e) => setContactForm({ ...contactForm, contact_name: e.target.value })}
                required
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="contact_email">Your Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={contactForm.contact_email}
                onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
                required
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                rows={5}
                placeholder="Tell the business owner about your enquiry..."
                className="mt-2"
              />
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-accent-orange text-white hover:bg-[#D97000] rounded-full py-3"
            >
              {sending ? 'Sending...' : 'Send Enquiry'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BusinessDetailPage;
